"use client";

import * as React from "react";
import { LucideIcon } from "lucide-react";

/**
 * Mobile Toolbar Action
 *
 * Represents a button that can be mounted to the mobile bottom toolbar
 * from any page/component in the app.
 */
export interface MobileToolbarAction {
  id: string;
  icon: LucideIcon;
  label: string;
  onPress: () => void;
  badge?: number;
  disabled?: boolean;
  priority?: number; // Higher priority = shown first when space is limited
  variant?: "default" | "primary" | "danger";
}

interface MobileToolbarContextType {
  /**
   * Register an action to be shown in the mobile toolbar.
   * Returns a cleanup function to remove the action.
   */
  registerAction: (action: MobileToolbarAction) => () => void;
  /**
   * All currently registered actions from all active pages.
   */
  registeredActions: MobileToolbarAction[];
  /**
   * The actual height of the mobile toolbar in pixels (including safe area).
   * Updated by the MobileBottomNav component.
   */
  toolbarHeight: number;
  setToolbarHeight: (height: number) => void;
}

const MobileToolbarContext = React.createContext<MobileToolbarContextType | null>(null);

export function MobileToolbarProvider({ children }: { children: React.ReactNode }) {
  const [actions, setActions] = React.useState<Map<string, MobileToolbarAction>>(new Map());
  const [toolbarHeight, setToolbarHeight] = React.useState(0);

  const registerAction = React.useCallback((action: MobileToolbarAction) => {
    setActions(prev => new Map(prev).set(action.id, action));

    // Return cleanup function
    return () => {
      setActions(prev => {
        const next = new Map(prev);
        next.delete(action.id);
        return next;
      });
    };
  }, []);

  const value = React.useMemo(
    () => ({
      registerAction,
      registeredActions: Array.from(actions.values()).sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0)),
      toolbarHeight,
      setToolbarHeight,
    }),
    [registerAction, actions, toolbarHeight],
  );

  return <MobileToolbarContext value={value}>{children}</MobileToolbarContext>;
}

export function useMobileToolbar() {
  const context = React.useContext(MobileToolbarContext);
  if (!context) {
    throw new Error("useMobileToolbar must be used within MobileToolbarProvider");
  }
  return context;
}

/**
 * Hook to get the current mobile toolbar height in pixels.
 * Useful for adding bottom padding to content containers.
 */
export function useMobileToolbarHeight() {
  const { toolbarHeight } = useMobileToolbar();
  return toolbarHeight;
}
